
import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Clock, Calendar, User } from "lucide-react";
import { format } from "date-fns";

const ViewExamModal = ({ isOpen, onClose, exam }) => {
  if (!exam) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{exam.name}</DialogTitle>
          <DialogDescription>
            {exam.isTemplate ? (
              <Badge className="bg-purple-100 text-purple-800 border-purple-200 mt-1">
                Template
              </Badge>
            ) : (
              <Badge className="bg-blue-100 text-blue-800 border-blue-200 mt-1">
                Exam
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="font-medium text-gray-700">Details</h3>
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <span>{exam.totalTime} minutes</span>
              </div>
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-gray-500" />
                <span>Created by: {exam.createdBy?.name || "System"}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span>
                  Created on:{" "}
                  {exam.createdDate
                    ? format(new Date(exam.createdDate), "MMM dd, yyyy")
                    : "N/A"}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="font-medium text-gray-700">Configuration</h3>
              <div>
                <span className="text-sm text-gray-500">Language: </span>
                <span>{exam.language || "English"}</span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Questions: </span>
                <span>{exam.questions?.length || 0}</span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Warnings Allowed: </span>
                <span>{exam.warningsAllowed}</span>
              </div>
            </div>
          </div>

          {exam.description && (
            <div>
              <h3 className="font-medium text-gray-700 mb-1">Description</h3>
              <p className="text-gray-600">{exam.description}</p>
            </div>
          )}

          {exam.instructions && (
            <div>
              <h3 className="font-medium text-gray-700 mb-1">Instructions</h3>
              <p className="text-gray-600">{exam.instructions}</p>
            </div>
          )}

          {exam.categoryDistribution && exam.categoryDistribution.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-700 mb-2">
                Category Distribution
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {exam.categoryDistribution.map((cat, index) => (
                  <div
                    key={index}
                    className="border rounded-md p-3 bg-gray-50"
                  >
                    <div className="font-medium">{cat.category}</div>
                    <div className="text-sm text-gray-600">
                      {cat.count} questions ({cat.percentage}%)
                    </div>
                    {cat.difficulty && (
                      <div className="text-xs text-gray-500 mt-1">
                        Easy: {cat.difficulty.easy || 0}, Medium:{" "}
                        {cat.difficulty.medium || 0}, Hard:{" "}
                        {cat.difficulty.hard || 0}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {exam.proctoring && (
            <div>
              <h3 className="font-medium text-gray-700 mb-2">
                Proctoring Settings
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <span className="text-sm text-gray-500">Enabled: </span>
                  <span>{exam.proctoring.enabled ? "Yes" : "No"}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">
                    Capture Images:{" "}
                  </span>
                  <span>{exam.proctoring.captureImages ? "Yes" : "No"}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">
                    Track Tab Switching:{" "}
                  </span>
                  <span>
                    {exam.proctoring.trackTabSwitching ? "Yes" : "No"}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Track Focus: </span>
                  <span>{exam.proctoring.trackFocus ? "Yes" : "No"}</span>
                </div>
                {exam.proctoring.captureFrequency && (
                  <div>
                    <span className="text-sm text-gray-500">
                      Capture Frequency:{" "}
                    </span>
                    <span>{exam.proctoring.captureFrequency / 1000} seconds</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ViewExamModal;
